/**
 * firmaRedNedenleri.jsx — why a COMPANY document was rejected, for the personnel pages (backlog #737).
 *
 * A personnel row's `firmaBelgeGruplari[]` carries the status of each company document but not the reviewer's reason; the reason sits on
 * the document's own row, which `GET /firma/{id}/evraklar` (the read Firma Kartı > Evraklar uses, #212) hands out with every group document
 * (`belgeGruplari[].belgeler[].dosya.rejectReason`; the backend clears it when the document is reopened). The personnel pages read it
 * once and only when a rejected company document is actually on screen, so a company with nothing rejected costs no extra request.
 *
 *  · `redNedenHaritasi(evraklar)` — { [fileTypeId]: reason } of the rejected group documents.
 *  · `useFirmaRedNedenHaritasi({ firmaId, fuarId, etkin })` — the map, read when `etkin` turns true (a live fair and a rejected document).
 *  · `FirmaRedNedenleriProvider` / `useFirmaRedNedenleri(gruplar)` — the list: the provider (around the company's Personel list) holds one
 *    read; a row's company-document squares ask for it only if one of their documents is rejected. Outside a provider (staff lists)
 *    nothing is read.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { firmaService } from "../../services/firmaService";

/** Reddedildi, grup durumlarının sayısal sözlüğünde (0 Beklemede / 1 Onaylandı / 2 Reddedildi). */
const REDDEDILDI = 2;

/** `GET /evraklar` cevabından { fileTypeId: neden } — yalnızca reddedilmiş ve nedeni olan grup belgeleri. */
export function redNedenHaritasi(evraklar) {
  const harita = {};
  (evraklar?.belgeGruplari || []).forEach((grup) => {
    (grup.belgeler || []).forEach((belge) => {
      const neden = belge?.dosya?.rejectReason;
      if (belge?.status === REDDEDILDI && typeof neden === "string" && neden.trim()) harita[belge.fileTypeId] = neden.trim();
    });
  });
  return harita;
}

/** Satırın firma belge gruplarında reddedilmiş bir belge var mı. */
export function reddedilmisFirmaBelgesiVar(gruplar) {
  return (gruplar || []).some((g) => (g.belgeler || []).some((b) => b?.status === REDDEDILDI));
}

export function useFirmaRedNedenHaritasi({ firmaId, fuarId, etkin = true }) {
  const [okunan, setOkunan] = useState({ anahtar: null, harita: {} });
  const anahtar = `${firmaId}|${fuarId}`;
  const calissin = Boolean(etkin && firmaId && Number(fuarId) > 0);
  useEffect(() => {
    if (!calissin) return undefined;
    let ignore = false;
    firmaService.getFirmaEvraklari(firmaId, fuarId)
      .then((evraklar) => { if (!ignore) setOkunan({ anahtar, harita: redNedenHaritasi(evraklar) }); })
      // The reason is a courtesy under a status the page already shows: a failed read leaves the plain "Reddedildi".
      .catch(() => { if (!ignore) setOkunan({ anahtar, harita: {} }); });
    return () => { ignore = true; };
  }, [calissin, firmaId, fuarId, anahtar]);
  return okunan.anahtar === anahtar ? okunan.harita : {};
}

const BOS_BAGLAM = { harita: {}, iste: () => {} };
const FirmaRedNedenleriBaglami = createContext(BOS_BAGLAM);

export function FirmaRedNedenleriProvider({ firmaId, fuarId, children }) {
  const [istendi, setIstendi] = useState(false);
  const iste = useCallback(() => setIstendi(true), []);
  const harita = useFirmaRedNedenHaritasi({ firmaId, fuarId, etkin: istendi });
  const deger = useMemo(() => ({ harita, iste }), [harita, iste]);
  return <FirmaRedNedenleriBaglami.Provider value={deger}>{children}</FirmaRedNedenleriBaglami.Provider>;
}

/**
 * Verilen firma belge gruplarının (bir satırın `firmaBelgeGruplari[]`ı) red nedenleri { fileTypeId: neden }. Grupların birinde reddedilmiş
 * bir belge varsa sağlayıcıdan okumayı ister; sağlayıcı yoksa (denetmen listeleri) hiçbir şey okunmaz ve harita boştur.
 */
export function useFirmaRedNedenleri(gruplar) {
  const { harita, iste } = useContext(FirmaRedNedenleriBaglami);
  const reddedilmisVar = reddedilmisFirmaBelgesiVar(gruplar);
  useEffect(() => { if (reddedilmisVar) iste(); }, [reddedilmisVar, iste]);
  return harita;
}
